import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { getCrews, getCrew, createCrew, updateCrew, deleteCrew } from '@/lib/api/crews';
import type { Crew, CrewMember } from '@/lib/api/crews';
import { getEmployees } from '@/lib/api/employees';
import type { Employee } from '@/lib/api/employees';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer, LargeTitleHeader, PullToRefresh, CollapsibleSection } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import apiClient from '@/lib/api/client';

// Crews — brought to the same completeness as Jobs/Clients: a full main-area
// table for the list, URL-driven detail (/crews/:id/:section) with accordion
// sections (Overview · Members · Schedule · Upcoming work), and ?new=1 support
// for the dashboard quick action.

const STATUS_OPTIONS = ['Available', 'On Job', 'Off Duty'] as const;
type CrewStatus = typeof STATUS_OPTIONS[number];

const statusBadge = (s: string) => {
  if (s === 'Available' || s === 'available') return 'bg-green-100 text-green-700';
  if (s === 'On Job' || s === 'on_job' || s === 'limited') return 'bg-blue-100 text-blue-700';
  return 'bg-gray-100 text-gray-500';
};
const statusLabel = (s: string) => {
  if (s === 'available') return 'Available';
  if (s === 'limited' || s === 'on_job') return 'On Job';
  if (s === 'unavailable' || s === 'Off Duty') return 'Off Duty';
  return s;
};

interface ScheduleEventRow {
  id: number;
  crew_id?: number;
  job_id?: number;
  job_title?: string;
  title?: string;
  scheduled_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status?: string;
  notes?: string | null;
}

export const CrewsPage = () => {
  const navigate = useNavigate();
  const { id: urlId, section: urlSection } = useParams<{ id?: string; section?: string }>();
  const [crews, setCrews] = useState<Crew[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<Crew | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showNewCrewModal, setShowNewCrewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', status: 'Available' as CrewStatus, lead_id: '' });
  const [newCrewForm, setNewCrewForm] = useState({ name: '', status: 'Available' as CrewStatus, lead_id: '' });
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [crewSearch, setCrewSearch] = useState('');
  const { toasts, removeToast, toast } = useToast();
  const [confirmDeleteCrew, setConfirmDeleteCrew] = useState(false);
  const crewNameRef = useRef<HTMLInputElement>(null);
  const crewSearchRef = useRef<HTMLInputElement>(null);
  // Schedule crew panel state
  interface JobOption { id: number; title: string; site_address?: string }
  const [schedJobs, setSchedJobs] = useState<JobOption[]>([]);
  const [schedJobId, setSchedJobId] = useState('');
  const [schedDate, setSchedDate] = useState('');
  const [schedEndDate, setSchedEndDate] = useState('');
  const [schedStart, setSchedStart] = useState('07:00');
  const [schedEnd, setSchedEnd] = useState('15:30');
  const [schedNotes, setSchedNotes] = useState('');
  const [schedBusy, setSchedBusy] = useState(false);
  const [schedMsg, setSchedMsg] = useState('');
  const [upcoming, setUpcoming] = useState<ScheduleEventRow[]>([]);

  void urlSection; // sections all render stacked; /crews/:id/:section stays a valid URL

  // ?new=1 auto-open (dashboard quick actions)
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('new') === '1') {
      setShowNewCrewModal(true);
      sp.delete('new');
      window.history.replaceState(null, '', window.location.pathname + (sp.toString() ? '?' + sp.toString() : ''));
    }
  }, []);

  // Keyboard shortcuts: "n" = new crew, "/" = focus search, Esc = close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (e.target as HTMLElement).isContentEditable;
    if (e.key === 'Escape') {
      if (showNewCrewModal) setShowNewCrewModal(false);
      return;
    }
    if (isInput) return;
    if (e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      setShowNewCrewModal(true);
    } else if (e.key === '/') {
      e.preventDefault();
      crewSearchRef.current?.focus();
    }
  }, [showNewCrewModal]);
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (showNewCrewModal) setTimeout(() => crewNameRef.current?.focus(), 50);
  }, [showNewCrewModal]);

  useEffect(() => {
    loadCrews();
    loadEmployees();
  }, []);

  // URL drives the selected crew (deep-linkable, Back works)
  useEffect(() => {
    if (urlId) loadCrewDetail(Number(urlId));
    else setSelectedCrew(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlId]);

  const loadCrews = async () => {
    try {
      setLoading(true);
      setCrews(await getCrews() || []);
    } catch {
      toast.error('Failed to load crews');
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try { setEmployees(await getEmployees() || []); } catch { /* non-fatal */ }
  };

  useEffect(() => {
    // /jobs returns {jobs, total, page…} — unwrap and keep open jobs only
    apiClient.get('/jobs?limit=200')
      .then(r => {
        const d = r.data?.data ?? r.data;
        const list: (JobOption & { status?: string })[] = Array.isArray(d) ? d : (Array.isArray(d?.jobs) ? d.jobs : []);
        setSchedJobs(list.filter(j => !['completed', 'cancelled'].includes(String(j.status || '').toLowerCase())));
      })
      .catch(() => {});
  }, []);

  // Upcoming work for the open crew (next 60 days of schedule events)
  useEffect(() => {
    if (!urlId) { setUpcoming([]); return; }
    apiClient.get<{ success: boolean; data: ScheduleEventRow[] }>('/schedule/events')
      .then(r => {
        const today = new Date().toISOString().slice(0, 10);
        setUpcoming((r.data.data ?? [])
          .filter(e => Number(e.crew_id) === Number(urlId) && (e.status || '') !== 'cancelled')
          .filter(e => ((e.end_date || e.scheduled_date) || '').slice(0, 10) >= today)
          .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))
          .slice(0, 12));
      })
      .catch(() => setUpcoming([]));
  }, [urlId, schedMsg]);

  const scheduleCrew = async () => {
    if (!selectedCrew || !schedJobId || !schedDate) { setSchedMsg('Job and date are required.'); return; }
    setSchedBusy(true);
    setSchedMsg('');
    try {
      const r = await apiClient.post<{ success: boolean; data: { id: number } }>('/schedule', {
        job_id: parseInt(schedJobId),
        crew_id: selectedCrew.id,
        scheduled_date: schedDate,
        end_date: schedEndDate || schedDate,
        start_time: schedStart,
        end_time: schedEnd,
        notes: schedNotes || undefined,
      });
      const schedId = r.data.data?.id;
      const userIds = (selectedCrew.members || []).map((m: CrewMember) => m.id).filter(Boolean);
      if (schedId && userIds.length > 0) {
        await apiClient.post('/schedule-assignments/bulk', { schedule_id: schedId, user_ids: userIds }).catch(() => {});
      }
      setSchedMsg(`Crew scheduled for ${schedDate}.`);
      setSchedDate('');
    } catch { setSchedMsg('Failed to create schedule entry. Check job selection.'); }
    finally { setSchedBusy(false); }
  };

  const loadCrewDetail = async (id: number) => {
    try {
      setLoadingDetail(true);
      const data = await getCrew(id);
      setSelectedCrew(data);
      setEditForm({ name: data.name, status: (data.status || 'Available') as CrewStatus, lead_id: String(data.lead_id || '') });
    } catch {
      toast.error('Failed to load crew details');
      navigate('/crews');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleCreateCrew = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCrew({
        name: newCrewForm.name,
        leadId: newCrewForm.lead_id ? Number(newCrewForm.lead_id) : undefined,
        status: newCrewForm.status,
      });
      toast.success('Crew created');
      setShowNewCrewModal(false);
      setNewCrewForm({ name: '', status: 'Available', lead_id: '' });
      await loadCrews();
    } catch {
      toast.error('Failed to create crew');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedCrew) return;
    try {
      await updateCrew(selectedCrew.id, {
        name: editForm.name,
        status: editForm.status,
        leadId: editForm.lead_id ? Number(editForm.lead_id) : undefined,
        members: (selectedCrew.members || []).map((m: CrewMember) => m.id),
      });
      toast.success('Crew saved');
      setEditMode(false);
      await loadCrews();
      await loadCrewDetail(selectedCrew.id);
    } catch {
      toast.error('Failed to save crew');
    }
  };

  const handleAddMember = async () => {
    if (!selectedCrew || !addMemberUserId) return;
    const uid = Number(addMemberUserId);
    const existing = (selectedCrew.members || []).map((m: CrewMember) => m.id);
    if (existing.includes(uid)) { toast.error('Already a member'); return; }
    try {
      await updateCrew(selectedCrew.id, {
        name: selectedCrew.name,
        status: selectedCrew.status,
        leadId: selectedCrew.lead_id,
        members: [...existing, uid],
      });
      setAddMemberUserId('');
      toast.success('Member added');
      await loadCrewDetail(selectedCrew.id);
      await loadCrews();
    } catch {
      toast.error('Failed to add member');
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!selectedCrew) return;
    const remaining = (selectedCrew.members || []).filter((m: CrewMember) => m.id !== userId).map((m: CrewMember) => m.id);
    try {
      await updateCrew(selectedCrew.id, {
        name: selectedCrew.name,
        status: selectedCrew.status,
        leadId: selectedCrew.lead_id,
        members: remaining,
      });
      toast.success('Member removed');
      await loadCrewDetail(selectedCrew.id);
      await loadCrews();
    } catch {
      toast.error('Failed to remove member');
    }
  };

  const confirmDeleteCrewAction = async () => {
    if (!selectedCrew) return;
    setConfirmDeleteCrew(false);
    try {
      await deleteCrew(selectedCrew.id);
      toast.success('Crew deleted');
      navigate('/crews');
      await loadCrews();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'Failed to delete crew');
    }
  };

  const cycleStatus = async () => {
    if (!selectedCrew) return;
    const cycle: CrewStatus[] = ['Available', 'On Job', 'Off Duty'];
    const cur = (statusLabel(selectedCrew.status) as CrewStatus) || 'Available';
    const next = cycle[(cycle.indexOf(cur) + 1) % cycle.length];
    try {
      await updateCrew(selectedCrew.id, {
        name: selectedCrew.name, status: next, leadId: selectedCrew.lead_id,
        members: (selectedCrew.members || []).map((m: CrewMember) => m.id),
      });
      await loadCrewDetail(selectedCrew.id);
      await loadCrews();
      toast.success(`Status → ${next}`);
    } catch { toast.error('Failed to update status'); }
  };

  const employeesWithUser = employees.filter(e => e.user_id);
  const currentMemberUserIds = new Set((selectedCrew?.members || []).map((m: CrewMember) => m.id));
  const availableToAdd = employeesWithUser.filter(e => !currentMemberUserIds.has(e.user_id!));
  const filteredCrews = useMemo(() => crewSearch
    ? crews.filter(c => c.name.toLowerCase().includes(crewSearch.toLowerCase()) || c.lead_name?.toLowerCase().includes(crewSearch.toLowerCase()))
    : crews, [crews, crewSearch]);

  const exportCrewsCsv = () => {
    const rows = [
      ['Crew', 'Status', 'Lead', 'Members'],
      ...filteredCrews.map(c => [
        c.name ?? '', statusLabel(c.status), c.lead_name ?? '', String(c.member_count ?? 0),
      ]),
    ];
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `crews-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success('Crews exported');
  };

  const fmtDate = (d?: string | null) =>
    d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '';

  // ── List view: jobs-style main-area table ──
  if (!urlId) {
    return (
      <MainLayout>
        <ToastContainer toasts={toasts} onRemove={removeToast} />
        <LargeTitleHeader
          title="Crews"
          subtitle={`${crews.length} crew${crews.length !== 1 ? 's' : ''} · team assignments`}
          actions={
            <div className="flex items-center gap-1.5">
              <button onClick={exportCrewsCsv} title="Export CSV"
                className="flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50">
                <span className="material-symbols-outlined text-base">download</span>
              </button>
              <button onClick={() => setShowNewCrewModal(true)} title="New crew (N)"
                className="flex items-center gap-1 rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90">
                <span className="material-symbols-outlined text-base">add</span>
                <span className="hidden sm:inline">New crew</span>
              </button>
            </div>
          }
        />
        <div className="relative">
          <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">search</span>
          <input
            ref={crewSearchRef}
            value={crewSearch}
            onChange={e => setCrewSearch(e.target.value)}
            placeholder="Search crews… (/)"
            className="w-full max-w-md border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            onKeyDown={e => { if (e.key === 'Escape') { setCrewSearch(''); (e.target as HTMLInputElement).blur(); } }}
          />
        </div>

        <PullToRefresh onRefresh={loadCrews}>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-400">
                <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
                Loading crews…
              </div>
            ) : filteredCrews.length === 0 ? (
              <div className="p-12 text-center">
                <span className="material-symbols-outlined text-gray-300 text-5xl block mb-3">groups</span>
                <p className="text-sm text-gray-500">{crewSearch ? 'No crews match your search.' : 'No crews yet — create your first one.'}</p>
              </div>
            ) : (
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
                    <th className="px-4 py-2.5 font-semibold">Crew</th>
                    <th className="px-3 py-2.5 font-semibold">Status</th>
                    <th className="px-3 py-2.5 font-semibold">Lead</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Members</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredCrews.map(crew => (
                    <tr key={crew.id} onClick={() => navigate(`/crews/${crew.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="bg-primary/10 rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-primary text-lg">groups</span>
                          </div>
                          <span className="font-semibold text-gray-900">{crew.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(crew.status)}`}>{statusLabel(crew.status)}</span>
                      </td>
                      <td className="px-3 py-3 text-gray-600">{crew.lead_name || '—'}</td>
                      <td className="px-3 py-3 text-right font-semibold text-gray-700">{crew.member_count ?? 0}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-0.5" onClick={e => e.stopPropagation()}>
                          <button onClick={() => navigate(`/crews/${crew.id}`)} title="Open"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center">
                            <span className="material-symbols-outlined text-lg">open_in_new</span>
                          </button>
                          <button onClick={() => navigate(`/crews/${crew.id}/schedule`)} title="Schedule this crew"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center">
                            <span className="material-symbols-outlined text-lg">event</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </PullToRefresh>

        {showNewCrewModal && renderNewCrewModal()}
      </MainLayout>
    );
  }

  // ── Detail view: accordion sections, same pattern as jobs/clients ──
  return (
    <MainLayout>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {loadingDetail || !selectedCrew ? (
        <div className="h-40 flex items-center justify-center text-sm text-gray-400">Loading crew…</div>
      ) : (
        <>
          <LargeTitleHeader
            title={selectedCrew.name}
            subtitle={`${statusLabel(selectedCrew.status)} · ${(selectedCrew.members || []).length} member${(selectedCrew.members || []).length !== 1 ? 's' : ''}${selectedCrew.lead_name ? ` · lead ${selectedCrew.lead_name}` : ''}`}
            onBack={() => navigate('/crews')}
            backLabel="Crews"
            actions={
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={cycleStatus} title="Click to cycle status"
                  className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border ${statusBadge(selectedCrew.status)}`}>
                  <span className="material-symbols-outlined text-base">swap_horiz</span>
                  <span className="hidden sm:inline">{statusLabel(selectedCrew.status)}</span>
                </button>
                <button onClick={() => setEditMode(true)}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                  <span className="material-symbols-outlined text-base">edit</span>
                  <span className="hidden sm:inline">Edit</span>
                </button>
                <button onClick={() => setConfirmDeleteCrew(true)}
                  className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">
                  <span className="material-symbols-outlined text-base">delete</span>
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>
            }
          />

          <div className="space-y-3">
            <CollapsibleSection title="Overview" icon="groups" defaultOpen={true}>
              {editMode ? (
                <div className="space-y-3 max-w-md">
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">Crew name</span>
                    <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </label>
                  <div className="flex gap-3 flex-wrap">
                    <label className="block">
                      <span className="text-xs font-medium text-gray-500">Status</span>
                      <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value as CrewStatus }))}
                        className="mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-500">Lead</span>
                      <select value={editForm.lead_id} onChange={e => setEditForm(f => ({ ...f, lead_id: e.target.value }))}
                        className="mt-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                        <option value="">No lead</option>
                        {employeesWithUser.map(e => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveEdit} className="bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90">Save changes</button>
                    <button onClick={() => setEditMode(false)} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-2">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    ['Status', statusLabel(selectedCrew.status)],
                    ['Lead', selectedCrew.lead_name || '—'],
                    ['Members', String((selectedCrew.members || []).length)],
                    ['Upcoming events', String(upcoming.length)],
                  ].map(([l, v]) => (
                    <div key={l} className="bg-gray-50 rounded-lg px-3 py-2.5">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{l}</p>
                      <p className="text-sm font-semibold text-gray-900 mt-0.5">{v}</p>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection title="Team Members" icon="person" defaultOpen={true}
              headerActions={availableToAdd.length > 0 ? (
                <div className="flex items-center gap-2">
                  <select value={addMemberUserId} onChange={e => setAddMemberUserId(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white min-w-[140px]">
                    <option value="">Add employee…</option>
                    {availableToAdd.map(e => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
                  </select>
                  <button onClick={handleAddMember} disabled={!addMemberUserId}
                    className="text-sm font-medium bg-primary text-white px-3 py-1.5 rounded-lg hover:bg-primary/90 disabled:opacity-40">Add</button>
                </div>
              ) : undefined}>
              {(selectedCrew.members || []).length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-6">No members yet — add employees from the selector above.</p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {(selectedCrew.members || []).map((member: CrewMember) => {
                    const emp = employees.find(e => e.user_id === member.id);
                    return (
                      <div key={member.id} className="flex items-center gap-3 px-1 py-2.5">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <button onClick={() => emp && navigate(`/employees/${emp.id}`)}
                            className={`text-sm font-medium text-gray-900 truncate ${emp ? 'hover:text-primary' : ''}`}>
                            {emp?.name || member.name}
                          </button>
                          <p className="text-xs text-gray-400">{emp?.role || member.role || 'Member'}</p>
                        </div>
                        {selectedCrew.lead_id === member.id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Lead</span>
                        )}
                        <button onClick={() => handleRemoveMember(member.id)} className="text-gray-300 hover:text-red-400" title="Remove from crew">
                          <span className="material-symbols-outlined text-base">person_remove</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CollapsibleSection>

            <CollapsibleSection title="Schedule This Crew" icon="event" defaultOpen={true}>
              <div className="grid grid-cols-2 gap-3 max-w-xl">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Job *</label>
                  <select value={schedJobId} onChange={e => setSchedJobId(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none">
                    <option value="">Select job…</option>
                    {schedJobs.map(j => <option key={j.id} value={j.id}>{j.title}{j.site_address ? ` — ${j.site_address}` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start Date *</label>
                  <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End Date</label>
                  <input type="date" value={schedEndDate} onChange={e => setSchedEndDate(e.target.value)} min={schedDate}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Start Time</label>
                  <input type="time" value={schedStart} onChange={e => setSchedStart(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">End Time</label>
                  <input type="time" value={schedEnd} onChange={e => setSchedEnd(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Task / Notes</label>
                  <input type="text" value={schedNotes} onChange={e => setSchedNotes(e.target.value)}
                    placeholder="e.g. Foundation framing — day 1"
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none" />
                </div>
              </div>
              {schedMsg && <p className={`mt-2 text-xs ${schedMsg.includes('Failed') ? 'text-red-600' : 'text-green-700'}`}>{schedMsg}</p>}
              <button onClick={scheduleCrew} disabled={schedBusy || !schedJobId || !schedDate}
                className="mt-3 flex items-center gap-2 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-40">
                <span className="material-symbols-outlined text-base">event_available</span>
                {schedBusy ? 'Scheduling…' : `Schedule ${selectedCrew.name}`}
              </button>
              <p className="mt-1.5 text-xs text-gray-400">Creates a schedule event and adds all current crew members as individual assignments. Workers get a 5 PM SMS with their timesheet link on the scheduled day.</p>
            </CollapsibleSection>

            <CollapsibleSection title="Upcoming Work" icon="upcoming" defaultOpen={true}>
              {upcoming.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Nothing scheduled ahead for this crew.</p>
              ) : (
                <div className="divide-y divide-gray-50">
                  {upcoming.map(e => (
                    <button key={e.id} onClick={() => e.job_id && navigate(`/jobs/${e.job_id}`)}
                      className="w-full flex items-center gap-3 px-1 py-2.5 text-left hover:bg-gray-50 rounded-lg">
                      <div className="bg-primary/10 rounded-lg px-2 py-1 text-center min-w-[3.4rem]">
                        <p className="text-[10px] text-primary font-bold uppercase leading-none">{fmtDate(e.scheduled_date).split(' ')[0]}</p>
                        <p className="text-base font-bold text-gray-900 leading-tight">{fmtDate(e.scheduled_date).split(' ')[1]}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{e.job_title || e.title || 'Job'}</p>
                        <p className="text-xs text-gray-400">
                          {fmtDate(e.scheduled_date)}{e.end_date && e.end_date !== e.scheduled_date ? ` – ${fmtDate(e.end_date)}` : ''}
                          {e.start_time ? ` · ${String(e.start_time).slice(0, 5)}` : ''}
                          {e.notes ? ` · ${e.notes}` : ''}
                        </p>
                      </div>
                      <span className="material-symbols-outlined text-gray-300">chevron_right</span>
                    </button>
                  ))}
                </div>
              )}
            </CollapsibleSection>
          </div>
        </>
      )}

      {showNewCrewModal && renderNewCrewModal()}
      {confirmDeleteCrew && selectedCrew && (
        <ConfirmDialog
          title="Delete Crew"
          message={`Delete crew "${selectedCrew.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteCrewAction}
          onCancel={() => setConfirmDeleteCrew(false)}
        />
      )}
    </MainLayout>
  );

  function renderNewCrewModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
          <form onSubmit={handleCreateCrew}>
            <div className="px-6 pt-5 pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">New Crew</h3>
                <button type="button" onClick={() => setShowNewCrewModal(false)} className="text-gray-400 hover:text-gray-600">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Crew Name *</label>
                  <input ref={crewNameRef} type="text" required value={newCrewForm.name}
                    onChange={e => setNewCrewForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Framing Crew A"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select value={newCrewForm.status} onChange={e => setNewCrewForm(f => ({ ...f, status: e.target.value as CrewStatus }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {employeesWithUser.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Crew Lead</label>
                    <select value={newCrewForm.lead_id} onChange={e => setNewCrewForm(f => ({ ...f, lead_id: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40">
                      <option value="">None</option>
                      {employeesWithUser.map(e => <option key={e.user_id} value={e.user_id}>{e.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-2">
              <button type="button" onClick={() => setShowNewCrewModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-lg hover:bg-primary/90">Create Crew</button>
            </div>
          </form>
        </div>
      </div>
    );
  }
};
